/**
 * Voice Assistant Utility
 * Uses Web Speech API for text-to-speech
 * Free, works in all modern browsers
 */

class VoiceAssistant {
    constructor() {
        this.synth = null;
        this.currentUtterance = null;
        this.isPlaying = false;
        this.rate = 1.0;
        this.pitch = 1.0;
        this.volume = 1.0;
        this.preferredVoice = null;
        this.onEndCallback = null;
        this.onStartCallback = null;
    }

    /**
     * Get synth instance (lazy initialization for SSR)
     */
    getSynth() {
        if (!this.synth && typeof window !== 'undefined' && 'speechSynthesis' in window) {
            this.synth = window.speechSynthesis;
        }
        return this.synth;
    }

    /**
     * Check if speech synthesis is available
     */
    isAvailable() {
        return typeof window !== 'undefined' && 'speechSynthesis' in window;
    }

    /**
     * Get available voices
     */
    getVoices() {
        const synth = this.getSynth();
        return synth ? synth.getVoices() : [];
    }

    /**
     * Set preferred voice by name
     */
    setVoice(voiceName) {
        const voices = this.getVoices();
        this.preferredVoice = voices.find(v =>
            v.name.toLowerCase().includes(voiceName.toLowerCase())
        );
    }

    /**
     * Set speech rate (0.1 to 10)
     */
    setRate(rate) {
        this.rate = Math.max(0.1, Math.min(10, rate));
    }

    /**
     * Set pitch (0 to 2)
     */
    setPitch(pitch) {
        this.pitch = Math.max(0, Math.min(2, pitch));
    }

    /**
     * Set volume (0 to 1)
     */
    setVolume(volume) {
        this.volume = Math.max(0, Math.min(1, volume));
    }

    /**
     * Speak text
     */
    speak(text, options = {}) {
        return new Promise((resolve, reject) => {
            if (!this.isAvailable()) {
                reject(new Error('Speech synthesis not available'));
                return;
            }

            // Stop any current speech
            this.stop();

            const utterance = new SpeechSynthesisUtterance(text);

            // Apply settings
            utterance.rate = options.rate || this.rate;
            utterance.pitch = options.pitch || this.pitch;
            utterance.volume = options.volume || this.volume;

            // Set voice
            if (this.preferredVoice) {
                utterance.voice = this.preferredVoice;
            } else {
                // Try to use a good English voice
                const voices = this.getVoices();
                const englishVoice = voices.find(v =>
                    v.lang.startsWith('en') && v.name.includes('Google')
                ) || voices.find(v => v.lang.startsWith('en'));

                if (englishVoice) {
                    utterance.voice = englishVoice;
                }
            }

            utterance.onstart = () => {
                this.isPlaying = true;
                if (this.onStartCallback) this.onStartCallback();
            };

            utterance.onend = () => {
                this.isPlaying = false;
                if (this.onEndCallback) this.onEndCallback();
                resolve();
            };

            utterance.onerror = (event) => {
                this.isPlaying = false;
                reject(event);
            };

            this.currentUtterance = utterance;
            const synth = this.getSynth();
            if (synth) synth.speak(utterance);
        });
    }

    /**
     * Speak a sequence of texts with delays
     */
    async speakSequence(texts, delayMs = 500) {
        for (let i = 0; i < texts.length; i++) {
            await this.speak(texts[i]);
            if (i < texts.length - 1) {
                await new Promise(r => setTimeout(r, delayMs));
            }
        }
    }

    /**
     * Stop speaking
     */
    stop() {
        const synth = this.getSynth();
        if (synth && synth.speaking) {
            synth.cancel();
        }
        this.isPlaying = false;
    }

    /**
     * Pause speaking
     */
    pause() {
        const synth = this.getSynth();
        if (synth && synth.speaking) {
            synth.pause();
            this.isPlaying = false;
        }
    }

    /**
     * Resume speaking
     */
    resume() {
        const synth = this.getSynth();
        if (synth && synth.paused) {
            synth.resume();
            this.isPlaying = true;
        }
    }

    /**
     * Set callbacks
     */
    onStart(callback) {
        this.onStartCallback = callback;
    }

    onEnd(callback) {
        this.onEndCallback = callback;
    }
}

// Create singleton instance
const voiceAssistant = new VoiceAssistant();

// Export both the class and singleton
export { VoiceAssistant };
export default voiceAssistant;
